import { z } from "zod";
import { MatchingPipelineRunIdPublicSchema } from "./branded-id.schemas.js";
import {
  MATCHING_PIPELINE_PHASES,
  MATCHING_RUN_STATUSES,
} from "../types/admin-snapshot.types.js";

export const matchingRunStatusSchema = z.enum(MATCHING_RUN_STATUSES);
export const matchingPipelinePhaseSchema = z.enum(MATCHING_PIPELINE_PHASES);

export const runStatsSchema = z.object({
  poolsGenerated: z.number(),
  averagePoolSize: z.number(),
  giniCoefficient: z.number(),
});

// Per-model token bucket for the run-level per-step cost roll-up. Mirrors the
// step-result `stats.tokens.byModel` shape the frontend already prices per model.
const snapshotRunStepTokensSchema = z.object({
  input: z.number(),
  output: z.number(),
  // Defaulted, not required: every run doc written before PLA-1850 has a
  // bucket without it.
  llmMs: z.number().default(0),
  // Defaulted, not required: every run doc written before PLA-1850 has a
  // bucket without it.
  calls: z.number().default(0),
  errors: z.number().optional(),
  provider429s: z.number().optional(),
  infra429s: z.number().optional(),
  cacheRead: z.number().optional(),
  cacheWrite: z.number().optional(),
  proxyInput: z.number().optional(),
  proxyOutput: z.number().optional(),
});

/**
 * Per-step lifecycle counts, one entry per engine step type, ordered by when
 * the step first appeared in the run. `type` is a plain string so new step
 * types flow through without a taxonomy edit. The active step is the one with
 * `running > 0`; its `succeeded + failed` over the entry total is an exact
 * (not estimated) progress fraction.
 *
 * `cached`/`maxMs`/`phase` and the token maps are the run-level roll-up sourced
 * from `run.progress` (PLA-1272) so the admin per-step table + cost columns no
 * longer need the per-participant step-result envelopes. All optional/additive:
 * absent on legacy payloads and on runs with no accumulator yet. `tokensByModel`
 * is this run's own spend; `reusedTokensByModel` is inherited (cache-hit) spend
 * — full cost = both, run cost = `tokensByModel`.
 */
export const snapshotRunStepSchema = z.object({
  type: z.string(),
  running: z.number(),
  succeeded: z.number(),
  failed: z.number(),
  cached: z.number().optional(),
  maxMs: z.number().optional(),
  phase: matchingPipelinePhaseSchema.optional(),
  tokensByModel: z.record(z.string(), snapshotRunStepTokensSchema).optional(),
  reusedTokensByModel: z
    .record(z.string(), snapshotRunStepTokensSchema)
    .optional(),
});

/**
 * Read-time per-step lifecycle roll-up for the latest run, one entry per step
 * type. Counts derive from each step-result's `effectiveStatus(doc) =
 * doc.status ?? "succeeded"` (legacy v1 docs without `status` count as
 * succeeded). The admin stepper reads `steps` for the active step's progress.
 */
export const snapshotRunProgressSchema = z.object({
  steps: z.array(snapshotRunStepSchema).optional(),
});

/**
 * Heartbeat-watchdog telemetry for the latest run. Surfaced regardless of run
 * status (unlike `statistics`, which is terminal-only) so the UI can show
 * liveness while a run is in flight. `null` until the first beat. The UI
 * derives `completed = succeeded + failed + cached` and judges
 * alive/stalled/dead against `heartbeatIntervalMs`.
 */
export const snapshotHeartbeatSchema = z.object({
  succeeded: z.number(),
  failed: z.number(),
  cached: z.number(),
  beats: z.number(),
  beatAt: z.coerce.date().nullable(),
  lastProgressAt: z.coerce.date().nullable(),
  lastSkewMs: z.number(),
  maxSkewMs: z.number(),
  lastCpuPercent: z.number(),
  maxCpuPercent: z.number(),
  llmInFlight: z.number(),
});

export const snapshotRunSchema = z.object({
  idPublic: MatchingPipelineRunIdPublicSchema,
  status: matchingRunStatusSchema,
  // Phase the run is in (or ended at); drives the admin progress stepper.
  // Optional so older snapshot payloads without the field still decode.
  currentPhase: matchingPipelinePhaseSchema.nullish(),
  // Phase the run stops at; the stepper truncates here. Optional for the same
  // back-compat reason — absent is treated as a full run.
  targetStage: matchingPipelinePhaseSchema.optional(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  processingTimeMs: z.number().nullable(),
  errorMessage: z.string().nullable(),
  statistics: runStatsSchema.nullable(),
  // Per-step run-level roll-up sourced from the run's own `progress` accumulator.
  runProgress: snapshotRunProgressSchema.optional(),
  heartbeat: snapshotHeartbeatSchema.nullish(),
  heartbeatIntervalMs: z.number().optional(),
});

/**
 * Slim run-list entry for the admin run selector — enough to label and pick a
 * run without the heavy per-run payload (`runProgress`, `cohortStepResults`,
 * `heartbeat`). Listed newest-first, unfiltered: every run is selectable.
 */
export const snapshotRunListEntrySchema = z.object({
  idPublic: MatchingPipelineRunIdPublicSchema,
  status: matchingRunStatusSchema,
  targetStage: matchingPipelinePhaseSchema.optional(),
  currentPhase: matchingPipelinePhaseSchema.nullish(),
  createdAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  // Present only on experiment runs (PLA-1227) — the selector renders it as a tag.
  experimentLabel: z.string().optional(),
});

export const MatchingEngineRunSnapshotSchema = z.object({
  selectedRun: snapshotRunSchema.nullable(),
  runs: z.array(snapshotRunListEntrySchema),
});

export type SnapshotRunStatistics = z.infer<typeof runStatsSchema>;
export type SnapshotRunProgress = z.infer<typeof snapshotRunProgressSchema>;
export type SnapshotRunStep = z.infer<typeof snapshotRunStepSchema>;
export type SnapshotRun = z.infer<typeof snapshotRunSchema>;
export type SnapshotRunListEntry = z.infer<typeof snapshotRunListEntrySchema>;
export type MatchingEngineRunSnapshot = z.infer<
  typeof MatchingEngineRunSnapshotSchema
>;
